import { Play } from 'lucide-react';
import type { DevServerId } from '../../lib/devServersRegistry';
import { useDevServers } from '../../hooks/useDevServers';

type DevServerControlProps = {
  serverId: DevServerId;
  /** inline = dot + optional play; block = slightly larger for cards */
  variant?: 'inline' | 'block';
};

export function DevServerControl({ serverId, variant = 'inline' }: DevServerControlProps) {
  const { snapshot, start, startErrors } = useDevServers();
  const server = snapshot.servers[serverId];

  if (!import.meta.env.DEV || !snapshot.available || !server) return null;

  const running = server.running;
  const starting = server.starting;
  const dotClass = running
    ? 'bg-[var(--green)]'
    : starting
      ? 'bg-[var(--mustard)] animate-pulse'
      : 'bg-[var(--coral)]';
  const statusLabel = running
    ? `Running on :${server.port}`
    : starting
      ? `Starting :${server.port}…`
      : `Not running (:${server.port})`;
  const err = startErrors[serverId] ?? server.lastError;

  const dotSize = variant === 'block' ? 'h-2.5 w-2.5' : 'h-2 w-2';

  return (
    <span
      className="inline-flex items-center gap-1 shrink-0"
      title={err ? `${statusLabel}\n${err}` : statusLabel}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span className={`rounded-full ${dotSize} ${dotClass}`} aria-hidden />
      {!running && !starting && server.cwdExists && server.nodeModules !== false && !server.statusOnly && !server.noStart && (
        <button
          type="button"
          onClick={() => void start(serverId)}
          className="inline-flex items-center justify-center rounded border border-charcoal-200 bg-[var(--bg-raised)] px-1 py-0 text-[10px] text-charcoal-600 hover:border-consulting-slate-blue-300 hover:text-consulting-slate-blue-700"
          title={`Start ${server.label}`}
          aria-label={`Start ${server.label}`}
        >
          <Play className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}
